import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { browserStore, emptyTrail, readTrail, readOriginal, record, standpointOf, walk, writeTrail } from './trail';
import type { Trail, TrailStore, Standpoint, Step } from './trail';

/*
 * THE JOURNEY, HELD FOR THE SESSION AND KEPT BETWEEN THEM.
 *
 * Read once on mount and written back on every change. The write is not debounced because a step
 * is a person opening a figure — a handful a minute at the very most — and a debounce would risk
 * losing the last one to a closed tab for no gain anybody could measure.
 */

export interface UseTrail {
  trail: Trail;
  /** Record that the reader opened a figure. Safe to call again for one already walked. */
  walked(step: Step): void;
  /** Record that they opened the original-language line under a translation. */
  openedOriginal(): void;
  /** The nearest they have come to the divine and the furthest out, looked up in TODAY'S map. */
  record(rungOf: (id: string) => number | null): { closestIn: string | null; furthestOut: string | null };
  /** How they have been reading — inferred from what they did, never asked. */
  standpoint: Standpoint;
}

export function useTrail(store: TrailStore | undefined = browserStore()): UseTrail {
  /* Read once. A ref rather than a dependency so a store identity that changes per render — which
     `browserStore()` as a default argument does — cannot re-read and wipe the session's walking. */
  const storeRef = useRef(store);
  const [trail, setTrail] = useState<Trail>(() => readTrail(storeRef.current));

  /* Skip the write that would otherwise fire for the state we just READ, which is a pointless
     round trip through JSON on every mount. */
  const loaded = useRef(false);
  useEffect(() => {
    if (!loaded.current) { loaded.current = true; return; }
    writeTrail(storeRef.current, trail);
  }, [trail]);

  const walked = useCallback((step: Step) => {
    setTrail(current => {
      const next = walk(current, step);
      /* Nothing new happened — an already-walked figure in already-seen texts. Returning the same
         object keeps React from re-rendering the map for a step that changed nothing. */
      const same = next.visited.length === current.visited.length
        && next.corpora.length === current.corpora.length
        && next.disputed === current.disputed;
      return same ? current : next;
    });
  }, []);

  const openedOriginal = useCallback(() => { setTrail(readOriginal); }, []);

  const recordFor = useCallback(
    (rungOf: (id: string) => number | null) => record(trail.visited, rungOf),
    [trail.visited],
  );

  const standpoint = useMemo(() => standpointOf(trail), [trail]);

  return { trail, walked, openedOriginal, record: recordFor, standpoint };
}

/** A trail that remembers nothing — for a caller with no storage, and for tests. */
export function noTrail(): UseTrail {
  return {
    trail: emptyTrail(),
    walked: () => {},
    openedOriginal: () => {},
    record: () => ({ closestIn: null, furthestOut: null }),
    standpoint: 'newcomer',
  };
}
